// C-2: Named constant for the complimentary gift note variant ID used throughout this file.
// If this product changes, update it here and in cart.js.
const GIFT_NOTE_VARIANT_ID = 42415039447121;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // W-2: Store the bound close handler once and reuse it so we can remove it later.
    this._boundOverlayClose = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this._boundOverlayClose);

    this.setHeaderCartIconAccessibility();
    this.setupCheckoutValidation();

    // Check URL parameters on page load
    this.checkURLAndOpen();
  }

  setupCheckoutValidation() {
    // Use event delegation on the cart-drawer element since the form gets replaced on cart updates
    this.addEventListener('submit', async (e) => {
      const form = e.target.closest('#CartDrawer-Form');
      if (!form) return;

      // B-1: Prevent native form submission immediately — before inspecting e.submitter.
      // If e.submitter is null (older browser or programmatic submit), we still block
      // the native submit so the cart is never sent to Shopify checkout without BYO
      // validation running first.
      e.preventDefault();

      const submitButton = e.submitter;
      if (!submitButton || submitButton.name !== 'checkout') return;

      // B-2: Prevent a second concurrent validation. The CartCheckoutButtonGuard used to
      // re-enable the checkout button mid-flight; even without the guard, a rapid double-click
      // could race. This flag ensures only one validation runs at a time.
      if (this._checkoutValidationInProgress) return;
      this._checkoutValidationInProgress = true;

      submitButton.disabled = true;
      submitButton.classList.add('loading');

      try {
        // S-1: Share a single GET /cart.js response between repairBYOBundles and syncGiftNote
        // instead of each method fetching independently.
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        const cartData = await cartResponse.json();

        await this.repairBYOBundles(cartData);
        await this.syncGiftNote(cartData);

        window.location.href = routes.checkout_url;
      } catch (error) {
        // W-5, W-6: Replace alert() with visible error element; use cartStrings for the message.
        this._showCheckoutError(
          error.message || window.cartStrings.cartIssue
        );
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
      } finally {
        // Always clear the in-progress flag so future checkout attempts can proceed
        // (e.g., after a validation error the user corrects their cart and tries again).
        this._checkoutValidationInProgress = false;
      }
    });
  }

  // W-5: Render an accessible error message inside the drawer instead of alert().
  _showCheckoutError(message) {
    let errorEl = this.querySelector('#CartDrawer-CheckoutError');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.id = 'CartDrawer-CheckoutError';
      errorEl.className = 'cart-drawer__checkout-error form__message';
      errorEl.setAttribute('role', 'alert');
      const footer = this.querySelector('.cart-drawer__footer');
      if (footer) footer.prepend(errorEl);
    }
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  // S-1: cartData is now passed in from setupCheckoutValidation to avoid a second fetch.
  async repairBYOBundles(cartData) {
    // Group items by bundle_id
    const bundles = new Map();
    cartData.items.forEach((item) => {
      const bundleId = item.properties?._bundle_id;
      if (!bundleId || !bundleId.startsWith('bundle_')) return;

      if (!bundles.has(bundleId)) {
        bundles.set(bundleId, { parent: null, sleeves: [] });
      }

      const bundle = bundles.get(bundleId);
      // Parent product has variant IDs stored as _variant_id1, _variant_id2, etc.
      if (item.properties?._variant_id1) {
        bundle.parent = item;
      } else if (item.properties?._hidden) {
        bundle.sleeves.push(item);
      }
    });

    // Remove orphaned child items (sleeves whose parent was removed from cart)
    const orphanedKeys = {};
    for (const [, bundle] of bundles) {
      if (!bundle.parent && bundle.sleeves.length > 0) {
        bundle.sleeves.forEach((item) => {
          orphanedKeys[item.key] = 0;
        });
      }
    }

    // Remove sleeve variants that match a bundle's expected flavors but have no _bundle_id.
    // These are rogue items (e.g., added via product page ATC or a third-party app) that
    // would otherwise slip through to the order unchecked. The missing-sleeve check below
    // will re-add them with the correct _bundle_id and _hidden properties.
    const bundledVariantIds = new Set();
    for (const [, bundle] of bundles) {
      if (!bundle.parent) continue;
      for (let i = 1; i <= 10; i++) {
        const variantId = bundle.parent.properties[`_variant_id${i}`];
        if (variantId) bundledVariantIds.add(String(variantId));
        else break;
      }
    }
    cartData.items.forEach((item) => {
      const bid = item.properties?._bundle_id;
      if (!bid?.startsWith('bundle_') && bundledVariantIds.has(String(item.variant_id))) {
        orphanedKeys[item.key] = 0;
      }
    });

    if (Object.keys(orphanedKeys).length > 0) {
      // W-12: Use the response from cart/update.js directly instead of issuing a second GET.
      const removeResponse = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: orphanedKeys }),
      });

      if (!removeResponse.ok) {
        // W-6: Error string — needs i18n, currently sourced from cartStrings.
        throw new Error(window.cartStrings.cartIssue);
      }
    }

    // Check each BYO bundle and add missing sleeves
    const itemsToAdd = [];

    for (const [bundleId, bundle] of bundles) {
      if (!bundle.parent) continue;

      // Get expected variant IDs from parent properties
      const expectedVariantIds = [];
      for (let i = 1; i <= 10; i++) {
        const variantId = bundle.parent.properties[`_variant_id${i}`];
        if (variantId) {
          expectedVariantIds.push(String(variantId));
        } else {
          break;
        }
      }

      // Get actual sleeve variant IDs in cart (accounting for quantity)
      const actualVariantIds = [];
      bundle.sleeves.forEach((item) => {
        for (let i = 0; i < item.quantity; i++) {
          actualVariantIds.push(String(item.variant_id));
        }
      });

      // Find missing variant IDs
      const missingVariantIds = [...expectedVariantIds];
      actualVariantIds.forEach((id) => {
        const index = missingVariantIds.indexOf(id);
        if (index > -1) {
          missingVariantIds.splice(index, 1);
        }
      });

      // Add missing sleeves to the items to add
      if (missingVariantIds.length > 0) {
        // Group by variant ID and count quantities
        const missingCounts = new Map();
        missingVariantIds.forEach((id) => {
          missingCounts.set(id, (missingCounts.get(id) || 0) + 1);
        });

        missingCounts.forEach((quantity, variantId) => {
          itemsToAdd.push({
            id: variantId,
            quantity,
            properties: {
              'Parent Product': 'Build Your Own Cookie Box',
              _hidden: true,
              _bundle_id: bundleId,
            },
          });
        });
      }
    }

    // Add any missing items to cart
    if (itemsToAdd.length > 0) {
      // W-3: Use routes.cart_add_url (Dawn convention) instead of manual path construction.
      const addResponse = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd }),
      });

      if (!addResponse.ok) {
        throw new Error(window.cartStrings.cartIssue);
      }
    }

    // Re-fetch the live cart and assert every BYO bundle is complete before allowing
    // checkout to proceed. This catches any scenario where the repair above did not
    // fully persist — silent API failures, stale state, or any other unexpected cause.
    const verifyResponse = await fetch(`${routes.cart_url}.js`);
    const verifiedCart = await verifyResponse.json();

    const verifiedBundles = new Map();
    verifiedCart.items.forEach((item) => {
      const bundleId = item.properties?._bundle_id;
      if (!bundleId || !bundleId.startsWith('bundle_')) return;
      if (!verifiedBundles.has(bundleId)) verifiedBundles.set(bundleId, { parent: null, sleeves: [] });
      const b = verifiedBundles.get(bundleId);
      if (item.properties?._variant_id1) b.parent = item;
      else if (item.properties?._hidden) b.sleeves.push(item);
    });

    for (const [, b] of verifiedBundles) {
      if (!b.parent) continue;
      const expected = [];
      for (let i = 1; i <= 10; i++) {
        const id = b.parent.properties[`_variant_id${i}`];
        if (id) expected.push(String(id));
        else break;
      }
      if (expected.length === 0) continue;
      const actual = [];
      b.sleeves.forEach((item) => {
        for (let i = 0; i < item.quantity; i++) actual.push(String(item.variant_id));
      });
      const remaining = [...expected];
      actual.forEach((id) => {
        const idx = remaining.indexOf(id);
        if (idx > -1) remaining.splice(idx, 1);
      });
      if (remaining.length > 0) {
        throw new Error('Your Build Your Own box is incomplete. Please refresh and try again.');
      }
    }
  }

  // S-1: cartData is now passed in from setupCheckoutValidation.
  // W-12: Avoid the second verification GET by trusting the update response.
  async syncGiftNote(cartData) {
    // C-2: Use named constant instead of magic number.
    const giftNoteItem = cartData.items.find(
      (item) => item.variant_id === GIFT_NOTE_VARIANT_ID
    );

    if (!giftNoteItem) {
      return;
    }

    const giftMessage = giftNoteItem.properties?.['Gift Note'] || '';
    const recipientEmail = giftNoteItem.properties?.['Recipient Email'] || '';
    const giftOccasion = giftNoteItem.properties?.['_Gift Occasion'] || '';

    if (cartData.note === giftMessage) {
      return;
    }

    // W-3: Use routes.cart_update_url.
    // W-12: Trust the update response — if it's ok, we don't re-fetch to verify.
    const updateResponse = await fetch(`${routes.cart_update_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        note: giftMessage,
        attributes: { RecipientEmail: recipientEmail, GiftOccasion: giftOccasion },
      }),
    });

    if (!updateResponse.ok) {
      throw new Error(window.cartStrings.giftSyncFailed);
    }

    const updatedCart = await updateResponse.json();
    if (updatedCart.note !== giftMessage) {
      throw new Error(window.cartStrings.giftSyncFailed);
    }
  }

  checkURLAndOpen() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cart-drawer') === 'open') {
      this.open();

      // Remove the parameter from URL without refreshing the page
      const newURL = new URL(window.location.href);
      newURL.searchParams.delete('cart-drawer');
      window.history.replaceState({}, '', newURL);
    }

    // S-4: Debug param — show_hidden exposes hidden BYO sleeve products. Active in production;
    // consider gating this behind a staff/preview condition before go-live.
    if (params.get('show_hidden') === 'true') {
      document.body.classList.add('show-hidden-items');
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Wait for cart images to finish loading before sliding in — prevents layout
    // shift from images popping in after the drawer is already visible.
    // A 600ms fallback ensures the drawer always opens even if an image stalls.
    const activate = () => {
      this.classList.add('animate', 'active');
      if (this._pendingPanel) {
        // Capture and reset the shipping bar fill inside the upsell panel so it
        // animates from 0% to its Liquid-rendered target width on panel open.
        const panelFill = this._pendingPanel.querySelector('.free-shipping-progress__fill');
        let panelFillTarget = null;
        if (panelFill) {
          panelFillTarget = panelFill.style.width;
          panelFill.style.transition = 'none';
          panelFill.style.width = '0%';
        }
        this._pendingPanel.classList.add('active');
        this._pendingPanel = null;
        if (panelFill && panelFillTarget) {
          requestAnimationFrame(() => {
            void panelFill.offsetWidth;
            panelFill.style.transition = '';
            panelFill.style.width = panelFillTarget;
          });
        }
      }
    };

    const images = Array.from(this.querySelectorAll('.drawer__inner img'));
    const unloaded = images.filter((img) => !img.complete);

    if (unloaded.length === 0) {
      setTimeout(activate);
    } else {
      let settled = 0;
      let done = false;
      const tryActivate = () => {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        setTimeout(activate);
      };
      const fallback = setTimeout(tryActivate, 600);
      unloaded.forEach((img) => {
        img.addEventListener('load', () => { settled++; if (settled >= unloaded.length) tryActivate(); }, { once: true });
        img.addEventListener('error', () => { settled++; if (settled >= unloaded.length) tryActivate(); }, { once: true });
      });
    }

    const drawerInner = this.querySelector('.drawer__inner');
    const onTransitionEnd = (event) => {
      if (event.target !== drawerInner) return;
      this.removeEventListener('transitionend', onTransitionEnd);
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = drawerInner || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', onTransitionEnd);

    this._scrollY = window.scrollY;
    document.body.style.top = `-${this._scrollY}px`;
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.top = '';
    window.scrollTo(0, this._scrollY || 0);
  }

  closeUpsell() {
    const upsellSection = this.querySelector('#CartUpsellSection');
    if (upsellSection) {
      removeTrapFocus();
      upsellSection.classList.remove('active');
      upsellSection.setAttribute('aria-hidden', 'true');
      // E1: Announce panel close
      const liveRegion = upsellSection.querySelector('#CartUpsellAnnouncement');
      if (liveRegion) liveRegion.textContent = 'Upgrade panel closed.';
    }

    // Trigger any shipping bar animation that was deferred while the upsell was visible.
    if (this._pendingShippingAnimation) {
      const { targetWidth } = this._pendingShippingAnimation;
      this._pendingShippingAnimation = null;
      // Re-query the fill element — the stored reference may be detached if a second
      // DOM update (e.g. GWP render) happened while the upsell was open.
      const fill = this.querySelector('.free-shipping-progress__fill');
      if (fill) {
        requestAnimationFrame(() => {
          void fill.offsetWidth;
          fill.style.transition = '';
          fill.style.width = targetWidth;
        });
      }
    }

    // Defensive fix: if cart rows exist, ensure drawer isn't stuck in empty-state UI.
    const hasCartItems = this.querySelectorAll('.cart-item').length > 0;
    if (hasCartItems) {
      this.classList.remove('is-empty');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove('is-empty');
      }
    }

    removeTrapFocus(this.activeElement);
  }

  // Reads the current qty for a line item's variant, decrements it by 1 (not zeroed out, so
  // other units of the same variant elsewhere in the cart are left intact), then adds the
  // upgrade variant. Mirrors the same decrement-then-add pattern used by addUpsellToCart().
  upgradeLineItem(button) {
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const currentVariantId = button.dataset.currentVariantId;
    let upgradeVariantId = button.dataset.upgradeVariantId;
    if (!currentVariantId || !upgradeVariantId) {
      console.error('Line item upgrade: missing variant ID on button');
      return;
    }

    // Tin GWP: if the upgrade target qualifies (e.g. GB08 -> GB12 during a 12 PK promo),
    // swap in the matching tin, same as a PDP add. CartDrawer.addToCart() applies the
    // promo discount code whenever the added variant is a Tin GWP tin.
    const tinVariantId = typeof window.getTinUpgradeVariantId === 'function'
      ? window.getTinUpgradeVariantId(button.dataset.upgradeVariantSku)
      : null;
    if (tinVariantId) upgradeVariantId = String(tinVariantId);

    // "12pk" = the plain 8pk->12pk upsell (cart-line-item-12pk-upsell.liquid), distinct from
    // the free-shipping-threshold upgrade (cart-line-item-upgrade.liquid) - same swap
    // mechanics below for both, but only the 12pk one is tracked as an order attribute, and
    // each gets its own event name so they can be reported on separately. This is only a
    // dataLayer push - it reaches GA4 via GTM-KMRTRLM's "Event - <name>" triggers and
    // "GA4 Event - <name>" tags, so any new param here also needs a GTM variable + tag param.
    const isPackSizeUpgrade = button.dataset.upgradeKind === '12pk';

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: isPackSizeUpgrade ? 'upgrade_to_12pk_click' : 'free_shipping_upgrade_click',
      upgrade_label: button.dataset.upgradeLabel || '',
      product_title: button.dataset.productTitle || '',
      current_variant_id: currentVariantId,
      current_variant_title: button.dataset.currentVariantTitle || '',
      upgrade_variant_id: upgradeVariantId,
      upgrade_variant_title: button.dataset.upgradeVariantTitle || '',
      price_diff_cents: button.dataset.priceDiffCents ? Number(button.dataset.priceDiffCents) : null,
    });

    const spinner = button.querySelector('.loading__spinner');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    spinner?.classList.remove('hidden');

    const qtyInput = document.querySelector(`.quantity__input[data-quantity-variant-id="${currentVariantId}"]`);
    const currentQty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    const newQty = Math.max(0, currentQty - 1);
    const updatePayload = { updates: { [currentVariantId]: newQty } };

    if (isPackSizeUpgrade) {
      // Order-visible record of the upgrade, readable in Shopify Admin's order "Additional
      // details" with no need for GA4 - /cart/update.js merges `attributes` per-key rather
      // than replacing the whole object, so this doesn't clobber other cart attributes set
      // elsewhere (gift messages, delivery date, etc). The prior value (if any) comes from
      // the button's own data attribute - rendered server-side from cart.attributes at page
      // load - rather than this response, so a second distinct 8pk->12pk swap later in the
      // same cart appends instead of overwriting the first.
      const attributeKey = 'Upgraded to 12pk';
      const existingValue = button.dataset.existingUpgradeNote || '';
      const productTitle = button.dataset.productTitle || button.dataset.upgradeVariantTitle || 'Product';
      const newValue = existingValue ? `${existingValue}; ${productTitle}` : productTitle;
      updatePayload.attributes = { [attributeKey]: newValue };
    }

    const body = JSON.stringify(updatePayload);

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to remove original item: ${response.status}`);
        }
        return response.json();
      })
      .then((cartData) => {
        if (!cartData || !cartData.items) {
          throw new Error('Invalid cart data after removing item');
        }
        return CartDrawer.addToCart({ items: [{ id: upgradeVariantId, quantity: 1 }] });
      })
      .catch((error) => {
        console.error('Error upgrading line item:', error);
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        spinner?.classList.add('hidden');
      });
  }


  addUpsellToCart() {
    const upsellType = document.querySelector('input[name="Upsell Type"]')?.value;
    if (upsellType === 'byo-tin') {
      return this.addByoTinUpsell();
    }

    const button = this.querySelector('#CartUpsellSection .button--primary');
    button.classList.add('loading');
    // W-7: Use string literals for aria-disabled values.
    button.setAttribute('aria-disabled', 'true');

    const loadingSpinner = this.querySelector('#CartUpsellSection .button--primary .loading__spinner');
    loadingSpinner.classList.remove('hidden');

    // W-8: Guard against null selectedVariantInput before accessing dataset.
    const selectedVariantInput = document.querySelector('input[name="Pack Size"]:checked')
      || document.querySelector('input[name="Size"]:checked');

    if (!selectedVariantInput) {
      console.error('No variant size input found for upsell');
      button.setAttribute('aria-disabled', 'false');
      button.classList.remove('loading');
      loadingSpinner.classList.add('hidden');
      return;
    }

    const selectedVariantId = selectedVariantInput.dataset.variantId;

    let upsellVariantId = document.querySelector('input[name="Upsell Variant Id"]').value;

    // If a two-option card layout is shown, check which tin the customer selected
    const upsellChoice = document.querySelector('input[name="upsell-choice"]:checked');
    if (upsellChoice && upsellChoice.value === 'secondary') {
      const secondaryId = document.querySelector('input[name="Upsell Variant Id Secondary"]')?.value;
      if (secondaryId) upsellVariantId = secondaryId;
    }

    if (upsellType === 'upsell_clothing') {
      const selectedVariantSku = selectedVariantInput.dataset.variantSku;
      const upsellSkuSuffix = selectedVariantSku.split('-')[1];
      const selectedUpsellSize = document.querySelector('input[name="upsellSize"]:checked').value;
      // TODO: Make this dynamic based on the product, right now it's hardcoded for Fashion Bundle
      const upsellPrefix = 'FASH' + selectedUpsellSize.toUpperCase() + selectedVariantSku.split('-')[0].replace('GB', '');

      // C-4: Guard against UPSELL_PRODUCT_4PK being undefined before accessing it.
      if (typeof UPSELL_PRODUCT_4PK === 'undefined') {
        console.error('UPSELL_PRODUCT_4PK is not defined');
        button.setAttribute('aria-disabled', 'false');
        button.classList.remove('loading');
        loadingSpinner.classList.add('hidden');
        return;
      }

      const upsellProduct = UPSELL_PRODUCT_4PK;
      upsellProduct.variants.forEach(variant => {
        if (variant.sku.split('-')[0].includes(upsellPrefix) && variant.sku.split('-')[1] === upsellSkuSuffix) {
          upsellVariantId = variant.id;
        }
      });
    }

    if (upsellType.includes('upsell')) {
      // Decrement by 1 rather than zeroing out all copies of this variant, so that
      // any pre-existing units of the same variant are left intact.
      // The quantity input rendered in the cart drawer already holds the live quantity,
      // so we can read it from the DOM without an extra fetch.
      const qtyInput = document.querySelector(`.quantity__input[data-quantity-variant-id="${selectedVariantId}"]`);
      const currentQty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      const newQty = Math.max(0, currentQty - 1);
      const body = JSON.stringify({ updates: { [selectedVariantId]: newQty } });

      fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to remove original item: ${response.status}`);
          }
          return response.json();
        })
        .then(cartData => {
          if (!cartData || !cartData.items) {
            throw new Error('Invalid cart data after removing item');
          }

          let payload = { items: [{ id: upsellVariantId, quantity: 1 }] };
          return CartDrawer.addToCart(payload);
        })
        .then(async () => {
          // After adding the upsell item (which may be a tin), run the full GWP
          // cycle: auto-add if the new item qualifies and no GWP exists yet, then enforce
          // the cap to remove any orphaned GWP if the new item does not qualify.
          if (window.GWP_ACTIVE && window.cartGWPUtils) {
            const sections = this.getSectionsToRender().map(s => s.id);
            const sectionsUrl = window.location.pathname;
            // autoAddGWP returns a partial cart/add.js response (newly added items
            // only). We must NOT pass that partial response into enforceGWPQuantityLimit,
            // or it will see "0 qualifying products, 1 GWP" and immediately remove the gift it
            // just added. Pass null so enforce always fetches the full live cart independently.
            let updatedCart;
            if (typeof window.cartGWPUtils.autoAddGWP === 'function') {
              updatedCart = await window.cartGWPUtils.autoAddGWP(null, {
                sections,
                sectionsUrl
              });
            }
            if (typeof window.cartGWPUtils.enforceGWPQuantityLimit === 'function') {
              const enforced = await window.cartGWPUtils.enforceGWPQuantityLimit({
                cartState: null,
                sections,
                sectionsUrl
              });
              // Prefer enforce result (has sections when it made changes); otherwise keep
              // the autoAdd result which has sections from the cart/add.js response.
              if (enforced && enforced.sections) updatedCart = enforced;
            }
            if (updatedCart && updatedCart.sections) {
              this.renderContents(updatedCart);
            }
          }
          button.setAttribute('aria-disabled', 'false');
          button.classList.remove('loading');
          loadingSpinner.classList.add('hidden');
          this.closeUpsell();
        })
        .catch(error => {
          console.error('Error in upsell process:', error);
          button.setAttribute('aria-disabled', 'false');
          button.classList.remove('loading');
          loadingSpinner.classList.add('hidden');
        });

    } else if (upsellType === 'cross-sell') {
      let payload = { items: [{ id: upsellVariantId, quantity: 1 }] };
      CartDrawer.addToCart(payload)
        .then(() => {
          button.setAttribute('aria-disabled', 'false');
          button.classList.remove('loading');
          loadingSpinner.classList.add('hidden');
          this.closeUpsell();
        })
        .catch(error => {
          console.error('Error in cross-sell process:', error);
          button.setAttribute('aria-disabled', 'false');
          button.classList.remove('loading');
          loadingSpinner.classList.add('hidden');
        });
    }
  }

  async addByoTinUpsell() {
    const button = this.querySelector('#CartUpsellSection .button--primary');
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');
    const spinner = button.querySelector('.loading__spinner');
    spinner?.classList.remove('hidden');

    const upsellVariantId = document.querySelector('input[name="Upsell Variant Id"]')?.value;
    const bundleId = document.querySelector('input[name="Upsell Bundle Id"]')?.value;

    if (!upsellVariantId) {
      console.error('No variant ID found for BYO tin upsell');
      button.setAttribute('aria-disabled', 'false');
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
      return;
    }

    try {
      const tinConfig = window.BYO_TIN_UPSELL
        ? Object.values(window.BYO_TIN_UPSELL).find(c => String(c.variantId) === String(upsellVariantId))
        : null;

      const tinProperties = { _byo_tin_upsell: 'true' };
      if (bundleId) tinProperties._bundle_id = bundleId;

      if (bundleId) {
        const cartRes = await fetch(`${routes.cart_url}.js`);
        const cartData = await cartRes.json();
        const parentItem = cartData.items.find(
          item => item.properties?._bundle_id === bundleId && item.properties?._is_bundle_parent === 'true'
        );

        if (parentItem) {
          // Copy sleeve SKUs from parent onto the tin
          let i = 1;
          while (parentItem.properties[`_sku${i}`]) {
            tinProperties[`_sku${i}`] = parentItem.properties[`_sku${i}`];
            i++;
          }

          // Link the tin back to the parent using dedicated properties — intentionally NOT
          // using the _sku{N}/_variant_id{N} indexed pattern, because repairBYOBundles()
          // treats every _variant_id{N} on the parent as an expected hidden sleeve and will
          // re-add a duplicate hidden tin at checkout if the tin's ID is in that list.
          const updatedParentProps = { ...parentItem.properties };
          if (tinConfig?.sku) updatedParentProps._tin_sku = tinConfig.sku;
          updatedParentProps._tin_variant_id = String(upsellVariantId);

          await fetch(routes.cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parentItem.key, properties: updatedParentProps }),
          });
        }
      }

      await CartDrawer.addToCart({ items: [{ id: upsellVariantId, quantity: 1, properties: tinProperties }] });

      if (window.GWP_ACTIVE && window.cartGWPUtils) {
        const sections = this.getSectionsToRender().map(s => s.id);
        const sectionsUrl = window.location.pathname;
        let updatedCart;
        if (typeof window.cartGWPUtils.autoAddGWP === 'function') {
          updatedCart = await window.cartGWPUtils.autoAddGWP(null, { sections, sectionsUrl });
        }
        if (typeof window.cartGWPUtils.enforceGWPQuantityLimit === 'function') {
          const enforced = await window.cartGWPUtils.enforceGWPQuantityLimit({
            cartState: null,
            sections,
            sectionsUrl
          });
          if (enforced && enforced.sections) updatedCart = enforced;
        }
        if (updatedCart && updatedCart.sections) {
          this.renderContents(updatedCart);
        }
      }
      button.setAttribute('aria-disabled', 'false');
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
      this.closeUpsell();
    } catch (error) {
      console.error('Error adding BYO tin upsell:', error);
      button.setAttribute('aria-disabled', 'false');
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Handle case where parsedState might be cart data instead of section data
    if (!parsedState) {
      return;
    }

    // Remove empty state if cart has items
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    // If parsedState has sections (from section rendering), use that
    if (parsedState.sections) {
      this.productId = parsedState.id;

      // Pre-capture shipping bar fill width before the innerHTML replacement.
      // The fill only exists in the DOM when cart != empty (see cart-drawer.liquid).
      // Default to '0%' so a first-add-to-empty-cart still animates from zero.
      const oldFillEl = this.querySelector('.free-shipping-progress__fill');
      const prevShipWidth = oldFillEl ? oldFillEl.style.width : '0%';

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        const sectionHTML = parsedState.sections[section.id];
        if (sectionHTML) {
          sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
        }
      });

      // Stage animation: pin the new fill at the old width (no transition) so it can
      // animate to the Liquid-rendered target. Actual triggering happens in the setTimeout
      // below — by that point, .finally() in product-form.js has already run and set
      // _pendingPanel if an upsell is going to be shown, letting us decide whether to
      // fire immediately or defer until closeUpsell().
      const newFill = this.querySelector('.free-shipping-progress__fill');
      if (newFill) {
        const targetShipWidth = newFill.style.width;
        newFill.style.transition = 'none';
        newFill.style.width = prevShipWidth;
        this._pendingShippingAnimation = { fill: newFill, targetWidth: targetShipWidth };
      }
    }
    // If parsedState is cart data (from addToCart), update cart items directly
    else if (parsedState.items) {
      // Update cart count in header - exclude hidden items
      const cartCountBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (cartCountBubble && parsedState.items) {
        const visibleItemCount = parsedState.items.reduce((count, item) => {
          return (item.properties && item.properties._hidden) || item.handle === 'urbanstems-postcard' ? count : count + item.quantity;
        }, 0);
        cartCountBubble.textContent = visibleItemCount;
      }

      // Update cart total
      const cartTotal = this.querySelector('.totals__total-value');
      if (cartTotal && parsedState.total_price !== undefined) {
        cartTotal.textContent = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(parsedState.total_price / 100);
      }

      // Update checkout button state - be more careful about when to disable
      const checkoutButton = this.querySelector('#CartDrawer-Checkout');
      if (checkoutButton && parsedState.items) {
        // Calculate visible items (exclude hidden bundle items)
        const visibleItemCount = parsedState.items.reduce((count, item) => {
          return (item.properties && item.properties._hidden) || item.handle === 'urbanstems-postcard' ? count : count + item.quantity;
        }, 0);

        if (visibleItemCount > 0) {
          checkoutButton.disabled = false;
        } else {
          checkoutButton.disabled = true;
        }
      }

    }

    // W-2: Re-use _boundOverlayClose (set in constructor) so we don't add duplicate listeners.
    // Add event listeners and open drawer
    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.removeEventListener('click', this._boundOverlayClose);
        overlay.addEventListener('click', this._boundOverlayClose);
      }
      this.open();
      if (typeof initCardIsleWidget === 'function') initCardIsleWidget();

      // Fire the staged shipping bar animation now — unless a panel is pending
      // (_pendingPanel set by .finally() microtask) OR already active (second cart update
      // while a panel is open). Both cases are deferred to close*Upsell() instead.
      const activeUpsellNow = document.getElementById('CartUpsellSection');
      const upsellIsVisible =
        this._pendingPanel ||
        (activeUpsellNow && activeUpsellNow.classList.contains('active'));
      if (!upsellIsVisible && this._pendingShippingAnimation) {
        const { fill, targetWidth } = this._pendingShippingAnimation;
        this._pendingShippingAnimation = null;
        requestAnimationFrame(() => {
          void fill.offsetWidth;
          fill.style.transition = '';
          fill.style.width = targetWidth;
        });
      }
    });
  }

  // S-2: getSectionInnerHTML and getSectionDOM are two separate helpers that both parse HTML.
  // They are kept separate to preserve existing call sites; getSectionDOM is used where a
  // full element reference is needed rather than just innerHTML.
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // C-3: addToCart is now a static method on CartDrawer — no longer an implicit global.
  // It is a well-structured async function that handles adding items via the Cart API with
  // proper error handling, loading state management, and accessible error feedback.
  // All call sites in this file use CartDrawer.addToCart(payload).
  // External call sites (component-cart-add-ons.js) access it as window.CartDrawer.addToCart
  // via the global class reference, which is valid since CartDrawer is registered on window
  // by customElements.define.
  static async addToCart(payload) {
    const body = JSON.stringify(payload);
    const cartDrawer = document.querySelector('cart-drawer');
    const checkoutButton = document.getElementById('CartDrawer-Checkout');

    // If a Tin GWP product is being added (e.g. the cart-drawer tin-upgrade upsell or
    // the BYO tin upsell), apply the discount code first so the tin renders discounted.
    if (payload && Array.isArray(payload.items)
        && typeof window.cartHasTinGWPProduct === 'function' && window.cartHasTinGWPProduct(payload.items)
        && typeof window.applyTinGWPDiscountCode === 'function') {
      await window.applyTinGWPDiscountCode();
    }

    let cartAddData;
    try {
      // Step 1: POST to cart/add.js
      const addResponse = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } });
      if (!addResponse.ok) {
        throw new Error(window.cartStrings.addFailed);
      }
      cartAddData = await addResponse.json();

      // Update cart contents from the add response
      if (cartDrawer) {
        cartDrawer.renderContents(cartAddData);
      }

      // Update checkout button state based on visible cart items
      if (checkoutButton && cartAddData.items) {
        const visibleItemCount = cartAddData.items.reduce((count, item) => {
          return item.properties && item.properties._hidden ? count : count + item.quantity;
        }, 0);
        checkoutButton.disabled = visibleItemCount === 0;
      }

      // Step 2: Fetch updated cart HTML to sync full UI
      // S-4: Preserve show_hidden debug param — see note in checkURLAndOpen.
      const showHiddenParam = window.location.search.includes('show_hidden=true') ? '&show_hidden=true' : '';
      const htmlResponse = await fetch(`${routes.cart_url}?section_id=cart-drawer${showHiddenParam}`);
      const responseText = await htmlResponse.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Replace the entire drawer__inner to get updated Liquid logic (including add-ons filtering)
      const targetInner = document.querySelector('.drawer__inner');
      const sourceInner = html.querySelector('.drawer__inner');

      // Capture the current shipping bar fill width before the DOM swap.
      const oldFill = targetInner?.querySelector('.free-shipping-progress__fill');
      const prevWidth = oldFill ? oldFill.style.width : null;

      // Save the Liquid-rendered target width from the NEW element, then overwrite
      // it with the old width (transition disabled) so the browser paints the old
      // state first after insertion.
      let targetWidth = null;
      if (prevWidth !== null && sourceInner) {
        const newFill = sourceInner.querySelector('.free-shipping-progress__fill');
        if (newFill) {
          targetWidth = newFill.style.width;
          newFill.style.transition = 'none';
          newFill.style.width = prevWidth;
        }
      }

      if (targetInner && sourceInner) {
        removeTrapFocus();
        targetInner.replaceWith(sourceInner);
        trapFocus(document.getElementById('CartDrawer'), sourceInner);

        // Force reflow at the old width, then re-enable transition and set the
        // Liquid-rendered target width so the CSS transition animates the change.
        // If the upsell panel is currently covering the cart, defer the animation
        // until closeUpsell() so it isn't playing invisibly in the background.
        const newFill = sourceInner.querySelector('.free-shipping-progress__fill');
        if (newFill && targetWidth !== null) {
          const activeUpsell = document.getElementById('CartUpsellSection');
          const anyPanelActive = activeUpsell && activeUpsell.classList.contains('active');
          if (anyPanelActive) {
            cartDrawer._pendingShippingAnimation = { fill: newFill, targetWidth };
          } else {
            void newFill.offsetWidth;
            newFill.style.transition = '';
            newFill.style.width = targetWidth;
          }
        }
      }

      // Step 3: Hide other popup sections after successful cart update
      const freeMessageSection = document.getElementById('CartFreeMessageSection');
      const giftSection = document.getElementById('CartGiftSection');

      if (freeMessageSection) {
        freeMessageSection.classList.remove('active');
        freeMessageSection.setAttribute('aria-hidden', 'true');
      }
      if (giftSection) {
        giftSection.classList.remove('active');
        giftSection.setAttribute('aria-hidden', 'true');
      }

      if (typeof initCardIsleWidget === 'function') initCardIsleWidget();

      return cartAddData;
    } catch (error) {
      console.error('Error in CartDrawer.addToCart:', error);
      // Re-enable checkout button on error so the user is not stuck.
      if (checkoutButton) {
        checkoutButton.disabled = false;
      }
      throw error;
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'product-form' || event.source === 'build-your-own') {
        return;
      }
      this.onCartUpdate();
    });
  }

  // W-4, S-5: disconnectedCallback cleans up the pub/sub subscription.
  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// The free-shipping-progress element is re-rendered via Liquid on every cart update
// (it lives inside .drawer__inner). Animation is handled by the pre-swap logic in
// addToCart / onCartUpdate / refreshCartDrawer which captures the old fill width,
// sets it on the new element with transition:none, then re-enables the transition
// via rAF so the CSS animates from old → new.
customElements.define('free-shipping-progress', class extends HTMLElement {});

class CartShowGiftSectionButton extends HTMLElement {
  connectedCallback() {
    this._handleClick = (event) => {
      event.preventDefault();
      const giftSection = document.getElementById('CartGiftSection');
      if (!giftSection) return;
      giftSection.classList.add('active');
      giftSection.setAttribute('aria-hidden', 'false');

      // B1: Trap focus inside the panel and handle ESC dismissal
      // W-11: 100ms delay is a focus-safety delay for newly-visible content, not a layout hack.
      setTimeout(() => {
        const firstFocusable = giftSection.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        trapFocus(giftSection, firstFocusable || giftSection);
      }, 100);

      // B1: ESC key dismissal
      this._escHandler = (e) => {
        if (e.key === 'Escape') {
          removeTrapFocus();
          giftSection.classList.remove('active');
          giftSection.setAttribute('aria-hidden', 'true');
          document.removeEventListener('keydown', this._escHandler);
          const showGiftButton = document.getElementById('GiftButton');
          if (showGiftButton) showGiftButton.focus();
          // E1: Announce panel close
          const liveRegion = document.getElementById('CartGiftSectionAnnouncement');
          if (liveRegion) liveRegion.textContent = 'Gift message panel closed.';
        }
      };
      document.addEventListener('keydown', this._escHandler);

      // E1: Announce panel open
      const liveRegion = document.getElementById('CartGiftSectionAnnouncement');
      if (liveRegion) liveRegion.textContent = 'Gift message panel opened.';

      // Opening the gift panel is the first point Card Isle is needed (see cart.js)
      if (typeof initCardIsleWidget === 'function') initCardIsleWidget({ requested: true });
    };

    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
    if (this._escHandler) document.removeEventListener('keydown', this._escHandler);
  }
}
customElements.define('cart-show-gift-section', CartShowGiftSectionButton);

class CartHideGiftSectionButton extends HTMLElement {
  connectedCallback() {
    // A1: custom element must be keyboard operable — delegate to the inner button
    const btn = this.querySelector('button');
    if (!btn) return;

    this._handleClick = () => {
      const giftSection = document.getElementById('CartGiftSection');
      if (!giftSection) return;
      removeTrapFocus();
      giftSection.classList.remove('active');
      giftSection.setAttribute('aria-hidden', 'true');

      // B2: Return focus to the button that opened the section
      const showGiftButton = document.getElementById('GiftButton');
      if (showGiftButton) {
        showGiftButton.focus();
      }

      // E1: Announce panel close
      const liveRegion = document.getElementById('CartGiftSectionAnnouncement');
      if (liveRegion) liveRegion.textContent = 'Gift message panel closed.';
    };

    btn.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    const btn = this.querySelector('button');
    if (btn && this._handleClick) {
      btn.removeEventListener('click', this._handleClick);
    }
  }
}
customElements.define('cart-hide-gift-section', CartHideGiftSectionButton);

class CartHideFreeMsgSectionButton extends HTMLElement {
  connectedCallback() {
    // A1: delegate to the inner button
    const btn = this.querySelector('button');
    if (!btn) return;

    this._handleClick = () => {
      const freeMessageSection = document.getElementById('CartFreeMessageSection');
      if (!freeMessageSection) return;
      removeTrapFocus();
      freeMessageSection.classList.remove('active');
      freeMessageSection.setAttribute('aria-hidden', 'true');

      // Restore cart drawer focus trap now that the panel is closed.
      const cartDrawerEl = document.getElementById('CartDrawer');
      const cartDrawerInner = cartDrawerEl && cartDrawerEl.querySelector('.drawer__inner');
      if (cartDrawerEl) trapFocus(cartDrawerEl, cartDrawerInner || cartDrawerEl);

      // B3: Restore focus to the button that opened the free message panel
      const freeMessageButton = document.getElementById('freeMessageButton');
      if (freeMessageButton) {
        freeMessageButton.focus();
      }

      // E1: Announce panel close
      const liveRegion = document.getElementById('CartFreeMessageAnnouncement');
      if (liveRegion) liveRegion.textContent = 'Gift note panel closed.';
    };

    btn.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    const btn = this.querySelector('button');
    if (btn && this._handleClick) {
      btn.removeEventListener('click', this._handleClick);
    }
  }
}
customElements.define('cart-hide-free-gift-msg-section', CartHideFreeMsgSectionButton);

class CartShowFreeMsgSectionButton extends HTMLElement {
  connectedCallback() {
    this._handleClick = (event) => {
      event.preventDefault();
      const freeMessageSection = document.getElementById('CartFreeMessageSection');
      if (!freeMessageSection) return;
      freeMessageSection.classList.add('active');
      freeMessageSection.setAttribute('aria-hidden', 'false');

      // B1: Trap focus inside the panel
      // W-11: 100ms focus-safety delay.
      setTimeout(() => {
        const firstInput = freeMessageSection.querySelector('#RecipientEmail');
        trapFocus(freeMessageSection, firstInput || freeMessageSection);
      }, 100);

      // B1: ESC key dismissal
      this._escHandler = (e) => {
        if (e.key === 'Escape') {
          removeTrapFocus();
          freeMessageSection.classList.remove('active');
          freeMessageSection.setAttribute('aria-hidden', 'true');
          document.removeEventListener('keydown', this._escHandler);

          // Restore cart drawer focus trap now that the panel is closed.
          const cartDrawerEl = document.getElementById('CartDrawer');
          const cartDrawerInner = cartDrawerEl && cartDrawerEl.querySelector('.drawer__inner');
          if (cartDrawerEl) trapFocus(cartDrawerEl, cartDrawerInner || cartDrawerEl);

          const freeMessageButton = document.getElementById('freeMessageButton');
          if (freeMessageButton) freeMessageButton.focus();
          // E1: Announce panel close
          const liveRegion = document.getElementById('CartFreeMessageAnnouncement');
          if (liveRegion) liveRegion.textContent = 'Gift note panel closed.';
        }
      };
      document.addEventListener('keydown', this._escHandler);

      // E1: Announce panel open
      const liveRegion = document.getElementById('CartFreeMessageAnnouncement');
      if (liveRegion) liveRegion.textContent = 'Gift note panel opened.';
    };

    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
    if (this._escHandler) document.removeEventListener('keydown', this._escHandler);
  }
}
customElements.define('cart-show-free-gift-msg-section', CartShowFreeMsgSectionButton);

// Free Gift Message Logic
if (!customElements.get('cart-free-gift-message')) {
  customElements.define(
    'cart-free-gift-message',
    class CartFreeGiftMessage extends HTMLElement {
      constructor() {
        super();

        this.submitButton = this.querySelector('[type="submit"]');
        this.loadingSpinner = this.querySelector('.loading__spinner');
        this.form = this.querySelector('#CartFreeMessageForm');
        this._boundSubmit = this.onSubmitHandler.bind(this);
        if (this.form) this.form.addEventListener('submit', this._boundSubmit);

        this.occasionSelect = this.querySelector('#GiftOccasion');
        this.occasionOtherWrapper = this.querySelector('#GiftOccasionOtherWrapper');
        this.occasionOtherInput = this.querySelector('#GiftOccasionOther');
        this._boundOccasionChange = this.onOccasionChangeHandler.bind(this);
        if (this.occasionSelect) this.occasionSelect.addEventListener('change', this._boundOccasionChange);
      }

      // W-4: Clean up listener in disconnectedCallback.
      disconnectedCallback() {
        this.form?.removeEventListener('submit', this._boundSubmit);
        this.occasionSelect?.removeEventListener('change', this._boundOccasionChange);
      }

      // Mirrors the :not(:placeholder-shown) floating-label trick used by the text
      // fields in this form — selects have no such pseudo-class, so toggle it by hand.
      onOccasionChangeHandler(evt) {
        const wrapper = evt.target.closest('.gift-occasion-field');
        if (wrapper) wrapper.classList.toggle('has-value', evt.target.value !== '');

        const isOther = evt.target.value === 'Other';
        this.occasionOtherWrapper?.classList.toggle('hidden', !isOther);
        if (isOther) {
          this.occasionOtherInput?.focus();
        } else if (this.occasionOtherInput) {
          this.occasionOtherInput.value = '';
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // W-7: Use string literals for aria-disabled values.
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.loadingSpinner.classList.remove('hidden');

        const note = document.getElementById('GiftMessage').value;
        const RecipientEmail = document.getElementById('RecipientEmail').value;
        const GiftOccasionSelect = document.getElementById('GiftOccasion').value;
        const GiftOccasionOther = document.getElementById('GiftOccasionOther')?.value.trim() || '';
        const GiftOccasion = GiftOccasionSelect === 'Other' && GiftOccasionOther !== '' ? GiftOccasionOther : GiftOccasionSelect;

        // Store the current cart state to prevent checkout button from being disabled
        const currentCheckoutButton = document.getElementById('CartDrawer-Checkout');

        // Ensure checkout button is enabled at the start
        if (currentCheckoutButton) {
          currentCheckoutButton.disabled = false;
        }

        // First update cart attributes (order note)
        const attributesBody = JSON.stringify({
          attributes: { RecipientEmail, GiftOccasion },
          note
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body: attributesBody } })
          .then(response => {
            if (!response.ok) {
              throw new Error(window.cartStrings.giftSyncFailed);
            }
            return response.json();
          })
          .then(cartData => {
            // Verify the note was actually saved
            if (cartData.note !== note) {
              throw new Error(window.cartStrings.giftSyncFailed);
            }

            // W-6: Use window.cartStrings or DOM text update for user-facing strings.
            // 'Edit gift message' is UI copy — update via a data attribute or cartStrings in future.
            document.getElementById('GiftButton').textContent = 'Edit gift message';

            // E2: Announce save success via aria-live region
            const liveRegion = document.getElementById('CartFreeMessageAnnouncement');
            if (liveRegion) liveRegion.textContent = 'Gift note saved successfully.';

            return cartData;
          })
          .then(cartData => {
            // Only remove existing gift note if there are other items in cart
            // C-2: Use named constant instead of magic number.
            const hasOtherItems = cartData.items.some(item =>
              item.variant_id !== GIFT_NOTE_VARIANT_ID && item.quantity > 0
            );

            if (hasOtherItems) {
              // Remove existing complimentary gift notes
              return fetch(`${routes.cart_update_url}`, {
                ...fetchConfig(),
                ...{
                  body: JSON.stringify({
                    updates: {
                      [GIFT_NOTE_VARIANT_ID]: 0
                    }
                  })
                }
              });
            }
            return Promise.resolve();
          })
          .then(() => {
            // Then add the new gift note
            // C-2: Use named constant instead of magic number.
            const payload = {
              items: [{
                id: String(GIFT_NOTE_VARIANT_ID),
                quantity: 1,
                properties: { 'Gift Note': note, 'Recipient Email': RecipientEmail, '_Gift Occasion': GiftOccasion }
              }]
            };
            return CartDrawer.addToCart(payload);
          })
          .then(() => {
            // Ensure checkout button is enabled after successful addition
            if (currentCheckoutButton) {
              currentCheckoutButton.disabled = false;
            }
          })
          .catch((error) => {
            console.error('Error adding gift note:', error);
            // Ensure checkout button is enabled even on error
            if (currentCheckoutButton) {
              currentCheckoutButton.disabled = false;
            }
          })
          .finally(() => {
            this.loadingSpinner.classList.add('hidden');
            this.submitButton.classList.remove('loading');
            // W-7: Use string literal for aria-disabled value.
            this.submitButton.setAttribute('aria-disabled', 'false');

            // Final check to ensure checkout button is enabled
            const finalCheckoutButton = document.getElementById('CartDrawer-Checkout');
            if (finalCheckoutButton) {
              finalCheckoutButton.disabled = false;
            }
          });
      }
    }
  );
}

// B-3: CartCheckoutButtonGuard was removed.
//
// It observed #CartDrawer-Checkout at page-load time and re-enabled the button if it
// became disabled while the cart still had items. This caused two concrete bugs:
//
// 1. It re-enabled the button mid-checkout-validation (after setupCheckoutValidation set
//    disabled=true), creating a window where a second click could start a concurrent
//    validation run — potentially adding duplicate sleeves or sending the user to checkout
//    before the first repair completed.
//
// 2. After any replaceWith() call (every cart mutation), the guard was observing a detached
//    DOM element. It could never re-enable the live checkout button, making it useless
//    after the first cart update.
//
// Button re-enabling on error is already handled by:
//   - setupCheckoutValidation: re-enables in the catch block
//   - cart.js removeBundleItems: re-enables in the finally block
//   - cart.js updateQuantity: re-enables in the finally block
//
// The _checkoutValidationInProgress flag in setupCheckoutValidation prevents double-submit.
